import { LayersIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router'

import type { ZoneSummary } from '@/api/types'
import { PageBody } from '@/components/AppShell'
import { EmptyState, ErrorState } from '@/components/states'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useActiveZone } from '@/zone/useActiveZone'

/**
 * Gate for every zone-scoped page. Renders its child only once a zone is
 * actually selected, so pages never have to reason about `zoneId === undefined`
 * or about the moment before the zone list has loaded.
 */
export function RequireZone({ children }: { children: (zone: ZoneSummary) => ReactNode }) {
  const { activeZone, isLoading, isError, error, refetch } = useActiveZone()

  if (isLoading) {
    return (
      <PageBody>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </PageBody>
    )
  }

  if (isError) {
    return (
      <PageBody>
        <ErrorState error={error} title="Could not load your zones" onRetry={refetch} />
      </PageBody>
    )
  }

  if (activeZone === undefined) {
    return (
      <PageBody>
        <EmptyState
          icon={LayersIcon}
          title="No zone selected"
          description="Every connection, channel and message belongs to a zone. You do not currently have one you can reach — an ADMIN on this account can create a zone or grant you access to an existing one."
          action={
            <Button asChild variant="outline">
              <Link to="/app/zones">Go to zones</Link>
            </Button>
          }
        />
      </PageBody>
    )
  }

  return <>{children(activeZone)}</>
}

/**
 * §17: a `DEACTIVATED` zone accepts no new connections, channels or messages,
 * but its history stays readable. Say so once, at the top, rather than letting
 * every write fail with a bare error.
 */
export function PausedZoneNotice({ zone }: { zone: ZoneSummary }) {
  if (zone.reachable) return null

  // An ACTIVE zone that is nevertheless unreachable can only mean the *account*
  // is not ACTIVE — §17 cascades "in effect, not in rows", so the zone row still
  // says ACTIVE while nothing in it works.
  if (zone.zone_state === 'ACTIVE') {
    return (
      <Alert variant="warning">
        <AlertTitle>This account is not active.</AlertTitle>
        <AlertDescription>
          No zone of a deactivated or archived account can be reached, whatever the zone&apos;s own
          state. Everything here stays readable; nothing new can be written until the account is
          reactivated.
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <Alert variant="warning">
      <AlertTitle>
        {zone.zone_state === 'ARCHIVED'
          ? 'This zone is archived and permanently read-only.'
          : 'This zone is deactivated.'}
      </AlertTitle>
      <AlertDescription>
        {zone.zone_state === 'ARCHIVED'
          ? 'Nothing can connect to it, and nothing new can be written. Its history is kept forever.'
          : 'No new connections, channels or messages are accepted while it is paused. Everything already here stays readable, and an ADMIN can reactivate it from the Zones page.'}
      </AlertDescription>
    </Alert>
  )
}
